import { PlpTypes } from 'lib/apis/services/PlpListing';
import { useHasAuth } from 'lib/DataStore';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { InsightEndPoint, InsightType } from '.';
import { sendRequest } from '../../core';
import { HPILandingResponse } from './type';

export namespace AvmPreview {
  const request = (body: InsightType.AvmPreviewBody) =>
    sendRequest<InsightType.AvmPreview>({
      method: 'post',
      version: 'v2',
      url: InsightEndPoint.AVM_PREVIEW_V2,
      body,
    });
  export const useAvmPreview = () => {
    return useMutation((postalCode: string) => {
      const body: InsightType.AvmPreviewBody = {
        uprn: postalCode,
      };
      return request(body).then((res) => res.data);
    });
  };
}

export namespace AvmValuation {
  const queryKey = (body: InsightType.AvmValuationBody) => [
    InsightEndPoint.AVM_VALUATION,
    body,
  ];
  const request = (body: InsightType.AvmValuationBody) =>
    sendRequest<InsightType.AvmValuationResponse>({
      method: 'post',
      url: InsightEndPoint.AVM_VALUATION,
      body,
      config: {
        headers: {
          Authorization: `${localStorage.getItem('accessToken')}`,
        },
      },
    });
  export const useAvmValuation = (body: InsightType.AvmValuationBody) => {
    const hasAuth = useHasAuth();
    return useQuery(
      queryKey(body),
      () => request(body).then((res) => res.data),
      {
        enabled: hasAuth,
        refetchOnMount: false,
        suspense: false,
      }
    );
  };

  export const useAvmValuationMutation = (
    body: InsightType.AvmValuationBody
  ) => {
    return useMutation({
      mutationFn: () => request(body).then((res) => res.data),
      mutationKey: [InsightEndPoint.AVM_VALUATION, body],
    });
  };
}

export namespace AvmMarketStat {
  const key = (body: InsightType.AvmMarketStatBody) => [
    InsightEndPoint.AVM_MARKET_STAT,
    body,
  ];
  const request = (body: InsightType.AvmMarketStatBody) => {
    return sendRequest<InsightType.AvmMarketStat>({
      method: 'post',
      url: InsightEndPoint.AVM_MARKET_STAT,
      body,
    });
  };
  export const useAvmMarketStat = (body: InsightType.AvmMarketStatBody) => {
    return useQuery(key(body), () => request(body).then((res) => res.data), {
      enabled: !!body.propertyUprn,
    });
  };
}

export namespace ClaimProperty {
  const claimPropertyRequest = (body: InsightType.ClaimPropertyBody) =>
    sendRequest<InsightType.ClaimProperty>({
      method: 'post',
      url: InsightEndPoint.CLAIM_PROPERTY,
      body,
    });

  export const useClaimPropertyMutation = (
    onSuccess: (_data: InsightType.ClaimProperty | null) => void
  ) => {
    return useMutation(
      (body: InsightType.ClaimPropertyBody) =>
        claimPropertyRequest(body).then((res) => res.data),
      {
        onSuccess,
      }
    );
  };
}

export namespace ClaimPropertyList {
  const key = () => [InsightEndPoint.CLAIM_PROPERTY_LIST];
  const request = () =>
    sendRequest<InsightType.ClaimPropertyList>({
      method: 'get',
      url: InsightEndPoint.CLAIM_PROPERTY_LIST,
    });

  export const useClaimPropertyList = () =>
    useQuery(key(), () => request().then((res) => res.data));
}

export namespace HPILanding {
  // const queryKey = () => [InsightEndPoint.MARKET_INSIGHT_LANDING];
  // TODO: remove countryId
  const request = () =>
    sendRequest<InsightType.HPILandingResponse>({
      method: 'get',
      url:
        InsightEndPoint.MARKET_INSIGHT_LANDING_V2,
      config: {
        params: {
          countryId: 2,
        },
      },
    });
  export const fetchData = (queryClient: QueryClient) =>
    queryClient.fetchQuery([InsightEndPoint.MARKET_INSIGHT_LANDING_V2], () =>
      request().then((res) => {

        return res.data;
      })
    );
  export const getData = (queryClient: QueryClient) =>
    queryClient.getQueryData<HPILandingResponse>([
      InsightEndPoint.MARKET_INSIGHT_LANDING_V2,
    ]);
}

export namespace MarketStat {
  const key = (params: InsightType.MarketStatParams) => [
    InsightEndPoint.MARKET_STAT_V2,
    params,
  ];
  const request = (params: InsightType.MarketStatParams) =>
    sendRequest<InsightType.MarketStateV2>({
      method: 'get',
      version: 'v2',
      url: InsightEndPoint.MARKET_STAT_V2,
      config: {
        params,
      },
    });

  export const fetchMarketStat = (
    queryClient: QueryClient,
    params: InsightType.MarketStatParams
  ) =>
    queryClient.fetchQuery(key(params), () =>
      request(params).then((res) => {
        return res.data;
      })
    );

  export const useMarketStat = (params: InsightType.MarketStatParams) => {
    return useQuery(
      key(params),
      () => request(params).then((res) => res.data),
      {
        enabled: !!params,
        keepPreviousData: true,
      }
    );
  };
}

export namespace Hierarchical {
  const key = (id: number) => [InsightEndPoint.HIERARCHICAL, id];
  const request = (id: number, type:string) => sendRequest({
      method: 'get',
      url: InsightEndPoint.HIERARCHICAL,
      config: {
        params: { id: id, type: type },
      },
    });
  export const fetchHierarchical = (queryClient: QueryClient, id: number, type:string) =>
    queryClient.fetchQuery(key(id), () => request(id, type).then((res) => {
      return res.data;
    }));

  export const useHierarchical = (id: number) => {
    const queryClient = useQueryClient();
    if (id)
      return queryClient.getQueryData<InsightType.HierarchicalResponse>(
        key(id)
      );
    else return undefined;
  };
}

export namespace SideLinks {
  const key = (region: number) => [InsightEndPoint.HIERARCHICAL_V2, region];
  const request = (region: number) =>
    sendRequest({
      method: 'get',
      url: InsightEndPoint.HIERARCHICAL_V2,
      config: {
        params: { region: region },
      },
    });
  export const fetchSideLinks = (queryClient: QueryClient, region: number) =>
    queryClient.fetchQuery(key(region), () =>
      request(region).then((res) => res.data)
    );
  export const useSideLinks = (region: number) => {
    const queryClient = useQueryClient();
    if (region)
      return queryClient.getQueryData<InsightType.SideLinksResponse>(
        key(region)
      );
    else return undefined;
  };
}

export namespace TrendsConfig {
  const key = (globalLocationId: string) => [
    InsightEndPoint.TRENDS_CONFIG,
    globalLocationId,
  ];
  const request = (globalLocationId: string) =>
    sendRequest<InsightType.TrendsConfig>({
      method: 'get',
      url: InsightEndPoint.TRENDS_CONFIG,
      config: {
        params: {
          globalLocationId,
          countryId: 2,
        },
      },
    });
  export const fetch = (
    queryClient: QueryClient,
    globalLocationId: string | undefined
  ) => {
    if (globalLocationId)
      return queryClient.fetchQuery(key(globalLocationId), () =>
        request(globalLocationId).then((res) => res.data)
      );
    else return undefined;
  };
  export const useTrendsConfig = (globalLocationId: string | undefined) => {
    const queryClient = useQueryClient();
    if (globalLocationId)
      return queryClient.getQueryData<InsightType.TrendsConfig>(
        key(globalLocationId)
      );
    return undefined;
  };
}

export namespace Trends {
  const key = (params: InsightType.TrendsParams) => [
    InsightEndPoint.TRENDS,
    params,
  ];
  const request = (params: InsightType.TrendsParams) =>
    sendRequest({
      method: 'get',
      url: InsightEndPoint.TRENDS,
      config: {
        params,
      },
    });
  export const useTrends = (params: InsightType.TrendsParams) => {
    return useQuery(
      key(params),
      () => request(params).then((res) => res.data),
      { enabled: !!params.globalLocationId && !!params.trendTypeIdentifier }
    );
  };
}

export namespace HpiSimilarListing {
  const key = (params: InsightType.SearchParamsFilter) => [
    InsightEndPoint.HPI_SIMILAR_LISTING,
    params,
  ];
  const request = (params: InsightType.SearchParamsFilter) => {
    return sendRequest<PlpTypes.plpListingResponse>({
      method: 'get',
      url: InsightEndPoint.HPI_SIMILAR_LISTING,
      config: {
        params,
      },
    });
  };
  export const useHpiSimilarListing = (
    params: InsightType.SearchParamsFilter
  ) => {
    return useQuery(
      key(params),
      () => request(params).then((res) => res.data),
      {
        enabled: !!params,
      }
    );
  };
}
